import { Table, Tag, Tooltip, Typography, Badge, Card, Space, Divider } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  UserOutlined,
  DollarOutlined,
  CalendarOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { HospitalityCustomer } from "../interfaces/hospitalityAnalytics.interface";

const { Text } = Typography;

interface HospitalityCustomerTableProps {
  customers: HospitalityCustomer[];
  loading: boolean;
  isMobile: boolean;
}

export default function HospitalityCustomerTable({
  customers,
  loading,
  isMobile,
}: HospitalityCustomerTableProps) {
  const formatCurrency = (amount: number) => {
    if (typeof amount !== "number" || isNaN(amount)) {
      return "€0.00";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (!date.isValid()) return "Invalid Date";
    return date.format("MMM DD, YYYY");
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (!date.isValid()) return "Invalid Date";
    return date.format("MMM DD, YYYY HH:mm");
  };

  const formatShortDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (!date.isValid()) return "Invalid Date";
    // Format: "Dec 5, 14:30" - shorter format for transaction list
    return date.format("MMM D, HH:mm");
  };

  const getSegmentColor = (segment: string) => {
    switch (segment) {
      case "vip":
        return "orange";
      case "regular":
        return "blue";
      case "occasional":
        return "purple";
      case "one-time":
        return "gold";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<HospitalityCustomer> = [
    {
      title: "Payment Account Reference",
      dataIndex: "paymentAccountReference",
      key: "paymentAccountReference",
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <Text code style={{ fontSize: "12px" }}>
            {text}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Segment",
      dataIndex: "segment",
      key: "segment",
      width: 120,
      filters: [
        { text: "VIP", value: "vip" },
        { text: "Regular", value: "regular" },
        { text: "Occasional", value: "occasional" },
        { text: "One-time", value: "one-time" },
      ],
      onFilter: (value, record) => record.segment === value,
      render: (segment: string) => (
        <Tag color={getSegmentColor(segment)} style={{ textTransform: "capitalize" }}>
          {segment}
        </Tag>
      ),
    },
    {
      title: "Visits",
      dataIndex: "visitCount",
      key: "visitCount",
      width: 100,
      sorter: (a, b) => a.visitCount - b.visitCount,
      render: (count: number) => (
        <Text strong>{count}</Text>
      ),
    },
    {
      title: "Total Spend",
      dataIndex: "totalSpend",
      key: "totalSpend",
      width: 140,
      sorter: (a, b) => a.totalSpend - b.totalSpend,
      render: (amount: number) => (
        <Text strong style={{ color: "#3f8600" }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: "Avg Transaction",
      dataIndex: "averageTransactionValue",
      key: "averageTransactionValue",
      width: 140,
      sorter: (a, b) => a.averageTransactionValue - b.averageTransactionValue,
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: "First Visit",
      dataIndex: "firstVisit",
      key: "firstVisit",
      width: 140,
      sorter: (a, b) =>
        new Date(a.firstVisit).getTime() - new Date(b.firstVisit).getTime(),
      render: (date: string) => (
        <Text>{formatDate(date)}</Text>
      ),
    },
    {
      title: "Last Visit",
      dataIndex: "lastVisit",
      key: "lastVisit",
      width: 140,
      sorter: (a, b) =>
        new Date(a.lastVisit).getTime() - new Date(b.lastVisit).getTime(),
      render: (date: string) => (
        <Text>{formatDate(date)}</Text>
      ),
    },
    {
      title: "Days Since Last Visit",
      dataIndex: "daysSinceLastVisit",
      key: "daysSinceLastVisit",
      width: 160,
      sorter: (a, b) => a.daysSinceLastVisit - b.daysSinceLastVisit,
      render: (days: number) => (
        <Text>{days} days</Text>
      ),
    },
    {
      title: "Visit Frequency",
      dataIndex: "visitFrequency",
      key: "visitFrequency",
      width: 140,
      sorter: (a, b) => a.visitFrequency - b.visitFrequency,
      render: (frequency: number) => (
        <Text>{frequency.toFixed(1)}/month</Text>
      ),
    },
    {
      title: "At Risk",
      dataIndex: "isAtRisk",
      key: "isAtRisk",
      width: 100,
      filters: [
        { text: "At Risk", value: true },
        { text: "Not At Risk", value: false },
      ],
      onFilter: (value, record) => record.isAtRisk === value,
      render: (isAtRisk: boolean) =>
        isAtRisk ? (
          <Badge status="error" text="Yes" />
        ) : (
          <Badge status="success" text="No" />
        ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <UserOutlined />
          <span>Customer List</span>
          <Tag color="blue">{customers.length} customers</Tag>
        </Space>
      }
      className="table_card"
    >
      <Table
        columns={columns}
        dataSource={customers.map((customer, index) => ({
          ...customer,
          key: customer.paymentAccountReference || `customer-${index}`,
        }))}
        loading={loading}
        scroll={{ x: "max-content" }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} customers`,
        }}
        expandable={{
          expandedRowRender: (record) => {
            if (!record.transactions || record.transactions.length === 0) {
              return (
                <div style={{ padding: 16 }}>
                  <Text type="secondary">No transaction data available</Text>
                </div>
              );
            }
            return (
              <div style={{ margin: 0, maxWidth: "100%", overflow: "hidden" }}>
                <Text strong style={{ fontSize: "14px", marginBottom: 12, display: "block" }}>
                  Recent Transactions (Last {record.transactions.length}):
                </Text>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%", maxWidth: "100%" }}
                >
                  {record.transactions.map((tx, txIdx) => (
                    <div
                      key={txIdx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: 4,
                        minWidth: 0, // Allow flex children to shrink
                        maxWidth: "100%",
                      }}
                    >
                      <Text
                        type="secondary"
                        ellipsis
                        style={{
                          flex: "1 1 auto",
                          minWidth: 0,
                          marginRight: 8,
                        }}
                      >
                        {formatShortDateTime(tx.transactionTime)}
                      </Text>
                      <Text
                        strong
                        style={{
                          color: "#3f8600",
                          flex: "0 0 auto",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatCurrency(tx.amount)}
                      </Text>
                    </div>
                  ))}
                </Space>
              </div>
            );
          },
          rowExpandable: (record) =>
            record.transactions && record.transactions.length > 0,
        }}
      />
    </Card>
  );
}

